import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal } from 'antd';
import { OPEN_ROOM } from 'gql/appointments/mutation';
import { useMutation } from '@apollo/client';
import { OpenRoom } from 'gql/appointments/__generated__/OpenRoom';

import { showErrorMessage } from 'utils/showErrorMessage';
import { instance } from 'utils/api';
import { Loading } from 'components/ui';
import { CopyLinkPassword } from 'components/shared';

export type CreateVideoCallModalRef = {
  onAppointmentCreated(id: string): void;
};

interface IProps {
  onClose(): void;
  onCreate(): void;
  onCreateAppointment(notes: string): void;
}

const CreateVideoCallModal: ForwardRefRenderFunction<
  CreateVideoCallModalRef,
  IProps
> = ({ onClose, onCreate, onCreateAppointment }, ref) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [isLoading, setLoading] = useState(false);
  const [link, setLink] = useState('');
  const [password, setPassword] = useState('');
  const [openRoom] = useMutation<OpenRoom>(OPEN_ROOM, {
    onError: err => {
      showErrorMessage(err);
      setLoading(false);
    },
    onCompleted(data) {
      const url = data?.openRoom.data.redirect_url;
      const roomId = data?.openRoom.data.meetUrl;

      setLink(`${url}/home/${roomId}?creator=doctor`);

      instance
        .post('meetings/create-room/doctor', {
          appointmentId: roomId
        })
        .then(res => {
          onCreate();
          setStep(2);
          setPassword(res?.data?.data?.password);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  });

  const renderModalContent = () => {
    if (isLoading) {
      return <Loading type="static" />;
    }

    switch (step) {
      case 1:
        return (
          <Form layout="vertical" form={form}>
            <Form.Item
              name="notes"
              label={t('video_call.note')}
              tooltip={t('video_call.note_only_for_you')}
            >
              <Input.TextArea
                placeholder={t('video_call.note')}
                style={{ minHeight: 127 }}
              />
            </Form.Item>
          </Form>
        );
      case 2:
        return <CopyLinkPassword link={link} password={password} />;
      default:
        return null;
    }
  };

  const onClickOk = () => {
    switch (step) {
      case 1: {
        setLoading(true);
        const notes = form.getFieldValue('notes');

        onCreateAppointment(notes);
        break;
      }

      default:
        onClose();
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    onAppointmentCreated: id => {
      openRoom({
        variables: { input: { id } }
      });
    }
  }));

  return (
    <Modal
      title={t('video_call.create_new')}
      onCancel={onClose}
      onOk={onClickOk}
      cancelText={t('common.cancel')}
      cancelButtonProps={{
        disabled: isLoading
      }}
      okButtonProps={{
        disabled: isLoading
      }}
      closable={!isLoading}
      open
    >
      {renderModalContent()}
    </Modal>
  );
};

export default forwardRef(CreateVideoCallModal);
